import { useTranslate, useMany } from '@refinedev/core';
import type { CrudFilter } from '@refinedev/core';
import { List } from '@refinedev/antd';
import { Table, Input, Form, Row, Col, Progress, Tooltip } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import type {
  PositionListOut,
  PublicationListOut,
  ProfileOut,
} from '@/api/generated/schemas';
import { ProductKind } from '@/api/generated/schemas';
import { useTableSearch } from '@/hooks/useTableSearch';
import { useTableRowNavigateToShow } from '@/hooks/useTableRowNavigateToShow';
import { useAdminTable } from '@/hooks/useAdminTable';
import { PublicationInfoPopover } from '@/components/user_catalog/publications/PublicationInfoPopover';
import { ProfileIconLink } from '@/components/account/ProfileIconLink';
import { TextFilter } from '@/components/common/TextFilter';
import { PublicationSelectFilter } from '@/components/user_catalog/publications/PublicationSelectFilter';
import { SelectFilter } from '@/components/common/SelectFilter';
import { DateRangeFilter } from '@/components/common/DateRangeFilter';
import {
  ProductFormatTag,
  ProductVariantTag,
} from '@/components/progress/tags';
import { orDash } from '@/components/common/show/shared';
import { formatDateTime } from '@/utils/format';
import { isFieldFiltered } from '@/utils/filters';
import { getSortOrder } from '@/utils/sorters';

export const PositionList = () => {
  const t = useTranslate();
  const rowNav = useTableRowNavigateToShow();
  const { onSearch, createDebouncedSubmit } = useTableSearch();

  const { tableProps, searchFormProps, sorters, setFilters, filters } =
    useAdminTable<PositionListOut>({
      pagination: { pageSize: 20 },
      resource: 'progress.positions',
      onSearch,
      sorters: {
        initial: [{ field: 'synced_at', order: 'desc' }],
      },
    });

  const debouncedSubmit = createDebouncedSubmit(searchFormProps.form);

  const rows = tableProps.dataSource ?? [];

  // Batched once per page: the row components take the record and skip their
  // own fetch, so a page costs two requests instead of one per row.
  const publicationIds = [
    ...new Set(rows.map((r) => r.publication_id).filter(Boolean)),
  ];

  const { result: publicationsResult } = useMany<PublicationListOut>({
    resource: 'user_catalog.publications',
    ids: publicationIds,
    queryOptions: { enabled: publicationIds.length > 0 },
  });

  const publicationMap = new Map<number, PublicationListOut>(
    (publicationsResult?.data ?? []).map((p: PublicationListOut) => [p.id, p])
  );

  const profileIds = [
    ...new Set(rows.map((r) => r.profile_id).filter(Boolean)),
  ];

  const { result: profilesResult } = useMany<ProfileOut>({
    resource: 'account.profiles',
    ids: profileIds,
    queryOptions: { enabled: profileIds.length > 0 },
  });

  const profileMap = new Map<number, ProfileOut>(
    (profilesResult?.data ?? []).map((p: ProfileOut) => [p.id, p])
  );

  return (
    <List>
      <Form {...searchFormProps} layout="vertical">
        <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
          <Col xs={24} sm={16} md={12} lg={8}>
            <Form.Item name="q" style={{ marginBottom: 0 }}>
              <Input
                placeholder={t('progress.positions.search.placeholder')}
                prefix={<SearchOutlined />}
                allowClear
                onChange={(e) => debouncedSubmit(e.target.value)}
              />
            </Form.Item>
          </Col>
        </Row>
      </Form>

      <Table
        {...rowNav('progress.positions', tableProps)}
        pagination={{ ...tableProps.pagination, simple: true }}
        rowKey="id"
      >
        <Table.Column
          dataIndex="id"
          title={t('base.fields.id')}
          sorter
          sortOrder={getSortOrder(sorters, 'id')}
        />

        <Table.Column
          dataIndex="profile_id"
          title={t('progress.positions.fields.profile_id')}
          render={(value: number) =>
            orDash(
              value != null && (
                <ProfileIconLink
                  profileId={value}
                  profile={profileMap.get(value)}
                />
              )
            )
          }
          filtered={isFieldFiltered(filters as CrudFilter[], 'profile_id')}
          filterDropdown={({ confirm, clearFilters }) => (
            <TextFilter
              field="profile_id"
              placeholder={t('progress.positions.filter.profile_id')}
              width={160}
              filters={filters as CrudFilter[]}
              confirm={confirm}
              clearFilters={clearFilters}
              setFilters={setFilters}
            />
          )}
        />

        <Table.Column
          dataIndex="publication_id"
          title={t('progress.positions.fields.publication_id')}
          render={(value: number) => (
            <PublicationInfoPopover
              publication={publicationMap.get(value)}
              publicationId={value}
            />
          )}
          filtered={isFieldFiltered(filters as CrudFilter[], 'publication_id')}
          filterDropdown={({ confirm, clearFilters }) => (
            <PublicationSelectFilter
              field="publication_id"
              placeholder={t('progress.positions.filter.publication_id')}
              filters={filters as CrudFilter[]}
              confirm={confirm}
              clearFilters={clearFilters}
              setFilters={setFilters}
            />
          )}
        />

        <Table.Column
          dataIndex="product_kind"
          title={t('progress.positions.fields.product_kind')}
          render={(value: string) =>
            orDash(
              value && <ProductFormatTag value={value} labels="positions" />
            )
          }
          filtered={isFieldFiltered(filters as CrudFilter[], 'product_kind')}
          filterDropdown={({ confirm, clearFilters }) => (
            <SelectFilter
              field="product_kind"
              minWidth={140}
              placeholder={t('progress.positions.filter.product_kind')}
              options={Object.values(ProductKind).map((k) => ({
                label: t(`progress.positions.format.${k}`),
                value: k,
              }))}
              filters={filters as CrudFilter[]}
              confirm={confirm}
              clearFilters={clearFilters}
              setFilters={setFilters}
            />
          )}
        />

        <Table.Column
          dataIndex="product_variant"
          title={t('progress.positions.fields.product_variant')}
          render={(value: string) =>
            orDash(
              value && <ProductVariantTag value={value} labels="positions" />
            )
          }
        />

        <Table.Column
          dataIndex="total_progression"
          title={t('progress.positions.fields.total_progression')}
          sorter
          sortOrder={getSortOrder(sorters, 'total_progression')}
          render={(value: number) =>
            orDash(
              value != null && (
                <Tooltip title={value}>
                  <Progress percent={Math.round(value * 100)} />
                </Tooltip>
              )
            )
          }
        />

        {/* Chronological: the device clock first (it resolves LWW conflicts),
            then when the server received it. */}
        <Table.Column
          dataIndex="client_timestamp"
          title={t('progress.positions.fields.client_timestamp')}
          sorter
          sortOrder={getSortOrder(sorters, 'client_timestamp')}
          render={(value: string) => orDash(formatDateTime(value))}
        />

        <Table.Column
          dataIndex="synced_at"
          title={t('progress.positions.fields.synced_at')}
          sorter
          sortOrder={getSortOrder(sorters, 'synced_at')}
          filtered={isFieldFiltered(filters as CrudFilter[], 'synced_at')}
          filterDropdown={({ confirm, clearFilters }) => (
            <DateRangeFilter
              field="synced_at"
              filters={filters as CrudFilter[]}
              confirm={confirm}
              clearFilters={clearFilters}
              setFilters={setFilters}
              showTime
            />
          )}
          render={(value: string | null) => orDash(formatDateTime(value))}
        />
      </Table>
    </List>
  );
};
